import { useEffect, useState } from "react";
import { z } from "zod";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from "@adora/shadcn-ui";
import { useToast } from "@adora/shared/hooks/use-toast";
import { useCreateWallet } from "../../hooks/use-token-admin";
import type { WalletType } from "../../types/token-wallet";

interface CreateWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: number;
  walletType: WalletType;
}

const schema = z
  .object({
    planName: z.string().trim().min(1, "Plan name is required").max(100),
    monthlyAllotment: z.number().int().min(0, "Must be 0 or greater"),
    rolloverCap: z.number().int().min(0, "Must be 0 or greater"),
    initialBalance: z.number().int().min(0, "Must be 0 or greater")
  })
  .refine((data) => data.rolloverCap >= data.monthlyAllotment, {
    message: "Rollover cap must be at least the monthly allotment",
    path: ["rolloverCap"]
  });

const EMPTY = { planName: "", monthlyAllotment: "", rolloverCap: "", initialBalance: "" };

export default function CreateWalletDialog({ open, onOpenChange, brandId, walletType }: CreateWalletDialogProps) {
  const { openToast } = useToast();
  const { mutate, isPending } = useCreateWallet(brandId);
  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setFields(EMPTY);
      setErrors({});
    }
  }, [open]);

  const handleSubmit = () => {
    const parsed = schema.safeParse({
      planName: fields.planName,
      monthlyAllotment: Number(fields.monthlyAllotment),
      rolloverCap: Number(fields.rolloverCap),
      initialBalance: Number(fields.initialBalance)
    });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as string] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    mutate(
      {
        brand_id: brandId,
        plan_name: parsed.data.planName,
        monthly_allotment: parsed.data.monthlyAllotment,
        rollover_cap: parsed.data.rolloverCap,
        initial_balance: parsed.data.initialBalance,
        wallet_type: walletType
      },
      {
        onSuccess: () => {
          openToast("Wallet created");
          onOpenChange(false);
        },
        onError: (error: Error) => openToast(error.message || "Failed to create wallet")
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Token Wallet</DialogTitle>
          <DialogDescription>Set up a generation-token wallet for this brand.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="plan-name">Plan name</Label>
            <Input
              id="plan-name"
              value={fields.planName}
              placeholder="e.g. growth"
              onChange={(e) => setFields((f) => ({ ...f, planName: e.target.value }))}
            />
            {errors.planName && <p className="text-sm text-destructive">{errors.planName}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="monthly-allotment">Monthly allotment</Label>
            <Input
              id="monthly-allotment"
              type="number"
              min={0}
              value={fields.monthlyAllotment}
              onChange={(e) => setFields((f) => ({ ...f, monthlyAllotment: e.target.value }))}
            />
            {errors.monthlyAllotment && <p className="text-sm text-destructive">{errors.monthlyAllotment}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rollover-cap">Rollover cap</Label>
            <Input
              id="rollover-cap"
              type="number"
              min={0}
              value={fields.rolloverCap}
              onChange={(e) => setFields((f) => ({ ...f, rolloverCap: e.target.value }))}
            />
            {errors.rolloverCap && <p className="text-sm text-destructive">{errors.rolloverCap}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="initial-balance">Initial balance</Label>
            <Input
              id="initial-balance"
              type="number"
              min={0}
              value={fields.initialBalance}
              onChange={(e) => setFields((f) => ({ ...f, initialBalance: e.target.value }))}
            />
            {errors.initialBalance && <p className="text-sm text-destructive">{errors.initialBalance}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "Creating…" : "Create Wallet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
